export const QUIZ_CSV_HEADERS = [
  "question_name",
  "option_one",
  "option_two",
  "option_three",
  "option_four",
  "option_five",
  "option_six",
  "information",
  "category",
  "language",
  "correct_answer",
  "answer_id",
  "showOption",
];

export const QUIZ_CSV_COLUMN_COUNT = QUIZ_CSV_HEADERS.length;

export const QUIZ_OPTION_MAX = 100;
export const QUIZ_QUESTION_MAX = 350;
export const QUIZ_INFO_MAX = 350;
export const QUIZ_PAGE_SIZES = [10, 25, 50, 100];

export const QUIZ_OPTION_KEYS = [
  "option_one",
  "option_two",
  "option_three",
  "option_four",
  "option_five",
  "option_six",
];

export const QUIZ_OPTION_LABELS = [
  "Option 1",
  "Option 2",
  "Option 3",
  "Option 4",
  "Option 5",
  "Option 6",
];

export const QUIZ_MIN_OPTIONS = 2;
export const QUIZ_MAX_OPTIONS = 6;
export const QUIZ_OPTION_COUNT_CHOICES = [2, 3, 4, 5, 6];

export const QUIZ_CATEGORIES = ["Snake", "Ladder"];
export const QUIZ_LANGUAGES = ["English", "Hindi", "Marathi"];

export function emptyQuizQuestion(id) {
  return {
    id,
    question_name: "",
    option_one: "",
    option_two: "",
    option_three: "",
    option_four: "",
    option_five: "",
    option_six: "",
    information: "",
    category: "",
    language: "English",
    correct_answer: "",
    answer_id: "",
    showOption: 2,
  };
}

export const QUIZ_MAX_QUESTIONS = 100;
/** Minimum questions required for the game. */
export const QUIZ_MIN_QUESTIONS = 10;
/** Minimum questions required to persist to the theme database (0 allows clearing incomplete rows). */
export const QUIZ_SAVE_MIN_QUESTIONS = 0;

export const SAMPLE_CSV_ROWS = [
  [
    "From where does Veeru propose to Basanti in Sholay?",
    "Top of a roof",
    "Top of a ladder",
    "Top of water tank",
    "Top of a hill",
    "",
    "",
    "",
    "Snake",
    "English",
    "Top of water tank",
    "3",
    "4",
  ],
  [
    "Which quality builds strong professional relationships?",
    "Trust",
    "Silence",
    "Conflict",
    "Apathy",
    "",
    "",
    "Trust helps teams work together.",
    "Ladder",
    "English",
    "Trust",
    "1",
    "4",
  ],
];

export function buildSampleCsvContent() {
  const escape = (val) => {
    const s = String(val ?? "");
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [
    QUIZ_CSV_HEADERS.join(","),
    ...SAMPLE_CSV_ROWS.map((row) => row.map(escape).join(",")),
  ];
  return lines.join("\r\n");
}

function downloadCsvBlob(content, filename) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function downloadSampleQuizCsv() {
  downloadCsvBlob(buildSampleCsvContent(), "quiz_questions_sample.csv");
}

export function exportQuizCsv(questions) {
  const escape = (val) => {
    const s = String(val ?? "");
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const rows = (questions || []).map((q) => {
    const c = clampQuizFields(q);
    return [
      c.question_name,
      c.option_one,
      c.option_two,
      c.option_three,
      c.option_four,
      c.option_five,
      c.option_six,
      c.information,
      c.category,
      c.language,
      c.correct_answer,
      c.answer_id,
      c.showOption,
    ]
      .map(escape)
      .join(",");
  });
  const content = [QUIZ_CSV_HEADERS.join(","), ...rows].join("\r\n");
  downloadCsvBlob(content, "quiz_questions_export.csv");
}

/** Parse a single CSV line respecting quoted fields */
function parseCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      out.push(cur.trim());
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  return out;
}

function normalizeHeader(h) {
  return String(h ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

const HEADER_ALIASES = {
  question_name: "question_name",
  question: "question_name",
  question_text: "question_name",
  option_one: "option_one",
  option1: "option_one",
  option_1: "option_one",
  option_two: "option_two",
  option2: "option_two",
  option_2: "option_two",
  option_three: "option_three",
  option3: "option_three",
  option_3: "option_three",
  option_four: "option_four",
  option4: "option_four",
  option_4: "option_four",
  option_five: "option_five",
  option5: "option_five",
  option_5: "option_five",
  option_six: "option_six",
  option6: "option_six",
  option_6: "option_six",
  information: "information",
  more_information: "information",
  more_info: "information",
  category: "category",
  language: "language",
  correct_answer: "correct_answer",
  correctanswer: "correct_answer",
  answer: "correct_answer",
  answer_id: "answer_id",
  showoption: "showOption",
  show_option: "showOption",
};

export const QUIZ_CSV_HEADER_REQUIRED_MESSAGE =
  "CSV cannot be uploaded without a header row. Row 1 must contain: question_name, option_one, option_two, option_three, option_four, option_five, option_six, information, category, language, correct_answer, answer_id, showOption. Leave unused options blank. Download Sample CSV for the correct format.";

function mapHeaderIndex(headers) {
  const index = {};
  headers.forEach((h, i) => {
    const key = HEADER_ALIASES[normalizeHeader(h)];
    if (key && index[key] === undefined) index[key] = i;
  });
  return index;
}

function looksLikeHeaderRow(cells) {
  const mapped = cells.map((c) => HEADER_ALIASES[normalizeHeader(c)] || normalizeHeader(c));
  const known = new Set(QUIZ_CSV_HEADERS);
  const matches = mapped.filter((m) => known.has(m)).length;
  return matches >= 4;
}

function validateHeaderRow(headerCells) {
  if (!looksLikeHeaderRow(headerCells)) {
    throw new Error(QUIZ_CSV_HEADER_REQUIRED_MESSAGE);
  }

  if (headerCells.length < 6) {
    throw new Error(
      `Invalid header row: expected columns (${QUIZ_CSV_HEADERS.join(", ")}), found ${headerCells.length}.`
    );
  }

  const col = mapHeaderIndex(headerCells);
  const required = ["question_name", "option_one", "option_two", "category", "language", "correct_answer"];
  const missing = required.filter((k) => col[k] === undefined);
  if (missing.length) {
    throw new Error(
      `Invalid header row. Missing columns: ${missing.join(", ")}. Expected: ${QUIZ_CSV_HEADERS.join(", ")}`
    );
  }

  return col;
}

export function parseQuizCsv(text) {
  const raw = String(text ?? "").replace(/^\uFEFF/, "").trim();
  if (!raw) {
    throw new Error("CSV file is empty.");
  }

  const lines = raw.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) {
    throw new Error(QUIZ_CSV_HEADER_REQUIRED_MESSAGE);
  }

  const headerCells = parseCsvLine(lines[0]);
  const col = validateHeaderRow(headerCells);

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const lineNum = i + 1;
    const cells = parseCsvLine(lines[i]);
    if (cells.every((c) => !c.trim())) continue;

    const cell = (key) => String(cells[col[key] ?? -1] ?? "").trim();
    const row = {
      question_name: cell("question_name"),
      option_one: cell("option_one"),
      option_two: cell("option_two"),
      option_three: cell("option_three"),
      option_four: cell("option_four"),
      option_five: cell("option_five"),
      option_six: cell("option_six"),
      information: cell("information"),
      category: cell("category"),
      language: cell("language"),
      correct_answer: cell("correct_answer"),
      answer_id: cell("answer_id"),
      showOption: cell("showOption"),
    };

    if (
      !row.question_name &&
      !row.option_one &&
      !row.option_two &&
      !row.option_three &&
      !row.option_four &&
      !row.option_five &&
      !row.option_six
    ) {
      continue;
    }
    rows.push(row);
  }

  if (!rows.length) {
    throw new Error("No question rows found in CSV (header row only).");
  }

  return rows;
}

function formatQuizRowMessage(rowLabel, text) {
  return rowLabel ? `${rowLabel} — ${text}` : text;
}

export function getQuizOptionValues(row) {
  return QUIZ_OPTION_KEYS.map((key) => String(row[key] ?? "").trim()).filter(Boolean);
}

/** Contiguous filled options from option_one upward (stops at first blank). */
export function getQuizOptionCount(row) {
  let count = 0;
  for (const key of QUIZ_OPTION_KEYS) {
    if (String(row?.[key] ?? "").trim()) count += 1;
    else break;
  }
  return count;
}

export function inferQuizOptionCount(row) {
  const count = getQuizOptionCount(row);
  if (count >= QUIZ_MIN_OPTIONS) return Math.min(QUIZ_MAX_OPTIONS, count);
  return QUIZ_MIN_OPTIONS;
}

/** Clear options beyond `count` and drop correct_answer if it was removed. */
export function applyQuizOptionCount(row, count) {
  const n = Math.min(QUIZ_MAX_OPTIONS, Math.max(QUIZ_MIN_OPTIONS, Number(count) || QUIZ_MIN_OPTIONS));
  const next = { ...row };
  QUIZ_OPTION_KEYS.forEach((key, i) => {
    if (i >= n) next[key] = "";
  });
  const options = getQuizOptionValues(next);
  const answer = String(next.correct_answer ?? "").trim();
  if (answer && !options.some((opt) => opt.toLowerCase() === answer.toLowerCase())) {
    next.correct_answer = "";
  }
  return next;
}

function validateOptionShape(row, rowLabel = "") {
  const vals = QUIZ_OPTION_KEYS.map((key) => String(row[key] ?? "").trim());
  let lastFilled = -1;
  for (let i = 0; i < vals.length; i++) {
    if (vals[i]) lastFilled = i;
  }

  if (lastFilled < QUIZ_MIN_OPTIONS - 1) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(
        rowLabel,
        `At least ${QUIZ_MIN_OPTIONS} options are required (Option 1 and Option 2).`
      ),
    };
  }

  for (let i = 0; i <= lastFilled; i++) {
    if (!vals[i]) {
      return {
        ok: false,
        code: "VALIDATION",
        message: formatQuizRowMessage(
          rowLabel,
          "Options must be filled in order without gaps. Leave later options blank when using only 2 or 3 choices."
        ),
      };
    }
  }

  return { ok: true, count: lastFilled + 1 };
}

export function normalizeQuizCategory(value) {
  const v = String(value ?? "").trim().toLowerCase();
  if (v === "snake" || v === "snakes") return "Snake";
  if (v === "ladder" || v === "ladders") return "Ladder";
  return "";
}

export function normalizeQuizLanguage(value) {
  const v = String(value ?? "").trim().toLowerCase();
  if (v === "english") return "English";
  if (v === "hindi") return "Hindi";
  if (v === "marathi") return "Marathi";
  return "";
}

export function clampQuizFields(row) {
  const questionName = String(
    row?.question_name ?? row?.question ?? row?.question_text ?? ""
  )
    .trim()
    .slice(0, QUIZ_QUESTION_MAX);

  const option_one = String(row?.option_one ?? "").trim().slice(0, QUIZ_OPTION_MAX);
  const option_two = String(row?.option_two ?? "").trim().slice(0, QUIZ_OPTION_MAX);
  const option_three = String(row?.option_three ?? "").trim().slice(0, QUIZ_OPTION_MAX);
  const option_four = String(row?.option_four ?? "").trim().slice(0, QUIZ_OPTION_MAX);
  const option_five = String(row?.option_five ?? "").trim().slice(0, QUIZ_OPTION_MAX);
  const option_six = String(row?.option_six ?? "").trim().slice(0, QUIZ_OPTION_MAX);
  const correct_answer = String(row?.correct_answer ?? "").trim().slice(0, QUIZ_OPTION_MAX);
  const options = [option_one, option_two, option_three, option_four, option_five, option_six];
  const showFromOptions = options.filter(Boolean).length;
  const showOption = Math.max(
    QUIZ_MIN_OPTIONS,
    Math.min(QUIZ_MAX_OPTIONS, Number(row?.showOption) || showFromOptions || QUIZ_MIN_OPTIONS)
  );
  const answerIndex = options.findIndex(
    (opt) => opt && opt.toLowerCase() === correct_answer.toLowerCase()
  );

  return {
    question_name: questionName,
    option_one,
    option_two,
    option_three,
    option_four,
    option_five,
    option_six,
    information: String(row?.information ?? "").trim().slice(0, QUIZ_INFO_MAX),
    category: normalizeQuizCategory(row?.category),
    language: normalizeQuizLanguage(row?.language) || "English",
    correct_answer,
    answer_id: answerIndex >= 0 ? String(answerIndex + 1) : String(row?.answer_id ?? "").trim(),
    showOption,
  };
}

const QUIZ_IMPORT_FIELD_LIMITS = [
  { key: "question_name", label: "Question (question_name)", max: QUIZ_QUESTION_MAX },
  ...QUIZ_OPTION_KEYS.map((key, i) => ({
    key,
    label: `${QUIZ_OPTION_LABELS[i]} (${key})`,
    max: QUIZ_OPTION_MAX,
  })),
  { key: "information", label: "More Information (information)", max: QUIZ_INFO_MAX },
  { key: "correct_answer", label: "Correct answer (correct_answer)", max: QUIZ_OPTION_MAX },
];

function validateCorrectAnswer(row, rowLabel = "") {
  const options = getQuizOptionValues(row);
  const answer = String(row.correct_answer ?? "").trim();
  if (!answer) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(rowLabel, "Correct answer is required."),
    };
  }
  const normalizedAnswer = answer.toLowerCase();
  const match = options.find((opt) => opt.toLowerCase() === normalizedAnswer);
  if (!match) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(
        rowLabel,
        "Correct answer must exactly match one of the filled options."
      ),
    };
  }
  return { ok: true, value: match };
}

/** showOption must be an integer 2–6 and equal the number of filled options. */
function validateShowOption(row, optionCount, rowLabel = "") {
  const raw = String(row.showOption ?? "").trim();
  if (!raw) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(
        rowLabel,
        `showOption is required and must be between ${QUIZ_MIN_OPTIONS} and ${QUIZ_MAX_OPTIONS}.`
      ),
    };
  }
  if (!/^\d+$/.test(raw)) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(
        rowLabel,
        `showOption must be a whole number between ${QUIZ_MIN_OPTIONS} and ${QUIZ_MAX_OPTIONS} (got "${raw}").`
      ),
    };
  }
  const n = parseInt(raw, 10);
  if (n < QUIZ_MIN_OPTIONS || n > QUIZ_MAX_OPTIONS) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(
        rowLabel,
        `showOption must be between ${QUIZ_MIN_OPTIONS} and ${QUIZ_MAX_OPTIONS} (got ${n}).`
      ),
    };
  }
  if (n !== optionCount) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(
        rowLabel,
        `showOption is ${n} but ${optionCount} option${optionCount === 1 ? " is" : "s are"} filled. showOption must match the number of options provided.`
      ),
    };
  }
  return { ok: true, value: n };
}

/**
 * answer_id must be the 1-based index of correct_answer among the filled options
 * (e.g. correct answer is option 2 → answer_id must be 2).
 */
function validateAnswerId(row, correctAnswer, optionCount, rowLabel = "") {
  const vals = QUIZ_OPTION_KEYS.map((key) => String(row[key] ?? "").trim()).slice(
    0,
    optionCount
  );
  const expected =
    vals.findIndex((opt) => opt.toLowerCase() === String(correctAnswer).toLowerCase()) + 1;

  if (expected < 1) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(
        rowLabel,
        "Correct answer must exactly match one of the filled options."
      ),
    };
  }

  const raw = String(row.answer_id ?? "").trim();
  if (!raw) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(
        rowLabel,
        `answer_id is required and must match the correct option number (expected ${expected} for this row).`
      ),
    };
  }
  if (!/^\d+$/.test(raw)) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(
        rowLabel,
        `answer_id must be a whole number from 1 to ${optionCount} (got "${raw}").`
      ),
    };
  }
  const id = parseInt(raw, 10);
  if (id < 1 || id > optionCount) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(
        rowLabel,
        `answer_id must be between 1 and ${optionCount} for this row (got ${id}).`
      ),
    };
  }
  if (id !== expected) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(
        rowLabel,
        `answer_id must be ${expected} because correct_answer matches option ${expected}, but got ${id}.`
      ),
    };
  }
  return { ok: true, value: String(id) };
}

/** Strict import checks — rejects over-length fields before saving. */
export function validateQuizRowForImport(row, rowLabel = "Row") {
  for (const { key, label, max } of QUIZ_IMPORT_FIELD_LIMITS) {
    const value = String(row[key] ?? "").trim();
    if (value.length > max) {
      return {
        ok: false,
        code: "CHAR_LIMIT",
        message: `${rowLabel} — ${label} has ${value.length} characters. Maximum allowed is ${max} characters. Please shorten the text and upload again.`,
      };
    }
  }

  if (!String(row.question_name ?? "").trim()) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(rowLabel, "Question name is required."),
    };
  }

  const shape = validateOptionShape(row, rowLabel);
  if (!shape.ok) return shape;

  const showCheck = validateShowOption(row, shape.count, rowLabel);
  if (!showCheck.ok) return showCheck;

  const answerCheck = validateCorrectAnswer(row, rowLabel);
  if (!answerCheck.ok) return answerCheck;

  const idCheck = validateAnswerId(row, answerCheck.value, shape.count, rowLabel);
  if (!idCheck.ok) return idCheck;

  const q = clampQuizFields({
    ...row,
    correct_answer: answerCheck.value,
    answer_id: idCheck.value,
    showOption: showCheck.value,
  });
  if (!q.category) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(rowLabel, "Category must be Snake or Ladder."),
    };
  }
  if (!q.language) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(rowLabel, "Language must be English, Hindi, or Marathi."),
    };
  }

  return {
    ok: true,
    data: {
      ...q,
      answer_id: idCheck.value,
      showOption: showCheck.value,
    },
  };
}

export function validateQuizRow(row, rowLabel = "", options = {}) {
  const { requireQuestion = true } = options;
  const normalized = clampQuizFields(row);

  if (requireQuestion && !normalized.question_name) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(
        rowLabel,
        "Question text is required. Open Edit and fill in the Question field."
      ),
    };
  }

  const shape = validateOptionShape(normalized, rowLabel);
  if (!shape.ok) return shape;

  const answerCheck = validateCorrectAnswer(normalized, rowLabel);
  if (!answerCheck.ok) return answerCheck;

  const q = clampQuizFields({
    ...normalized,
    correct_answer: answerCheck.value,
  });
  if (!q.category) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(rowLabel, "Category must be Snake or Ladder."),
    };
  }
  if (!q.language) {
    return {
      ok: false,
      code: "VALIDATION",
      message: formatQuizRowMessage(rowLabel, "Language must be English, Hindi, or Marathi."),
    };
  }

  return { ok: true, data: q };
}

export function validateQuizList(questions) {
  if (questions.length > QUIZ_MAX_QUESTIONS) {
    return { ok: false, message: `You can save up to ${QUIZ_MAX_QUESTIONS} questions.` };
  }

  if (!questions.length) {
    return { ok: true, data: [] };
  }

  const normalized = [];
  const missingQuestionIndexes = [];
  for (let i = 0; i < questions.length; i++) {
    const result = validateQuizRow(questions[i], `Question ${i + 1}`, {
      requireQuestion: true,
    });
    if (!result.ok) {
      if (/Question text is required/i.test(result.message)) {
        missingQuestionIndexes.push(i + 1);
        continue;
      }
      return { ok: false, message: result.message };
    }
    normalized.push(result.data);
  }

  if (missingQuestionIndexes.length) {
    const list = missingQuestionIndexes.join(", ");
    return {
      ok: false,
      message:
        missingQuestionIndexes.length === 1
          ? `Question ${list} is missing question text. Click Edit on that row, fill in Question, then save.`
          : `Questions ${list} are missing question text. Click Edit on each row, fill in Question, then save. You can also Delete incomplete rows and add them again.`,
    };
  }

  return { ok: true, data: normalized };
}
